import type { ProcessLaunchPackageAndroid } from '@playcastdotio/Messaging';
import * as Sentry from '@sentry/react';
import type {
  IPlayjectorService,
  InstanceLaunchedEvent,
} from '../../../services/playjector/interfaces/IPlayjectorService';
import type { GameInstanceAllocator } from '../GameInstanceAllocator';
import type {
  GameInstanceSessionRecord,
  GameInstanceStore,
} from '../GameInstanceStore';
import type { GameInstanceLaunchResult } from './GameInstanceLaunchManager';

/**
 * First-instance `instanceLaunched` reply budget on the android path
 * (android-multi-instance-host Phase 3, fixes the wedged-`launching` gap F3).
 * Sized for one cold VM boot + package open. Historically this flat value was
 * applied to every waiter; Playjector brings instances up sequentially, so the
 * wall-clock deadline must grow with `instanceCount` — see
 * {@link computeAndroidLaunchReplyTimeoutMs}.
 */
export const DEFAULT_ANDROID_LAUNCH_REPLY_TIMEOUT_MS = 120_000;

/** Matches C++ `AndroidLauncher` `kBootTimeout` (90s). */
export const ANDROID_LAUNCH_BOOT_BUDGET_MS = 90_000;

/** Matches C++ `OpenPackage` floor: `max(30s, readyWaitTimeMs/1000)`. */
export const ANDROID_LAUNCH_OPEN_FLOOR_MS = 30_000;

/**
 * Wall-clock budget for sequential Android `BringUpInstance` (one for-loop in
 * `AndroidLauncher`). All per-instance waiters share this deadline from launch
 * start — instance N only begins after 0..N-1 finish.
 *
 * `firstInstanceBudget (120s) + (n-1) * (bootBudget + openBudget)`, same shape
 * as `CampaignSwitchManager` (`base + readyWait`). `openBudget` is
 * `max(30s, readyWaitTimeMs)` to track the C++ package-open timeout.
 *
 * Examples (readyWaitTimeMs = 0): n=1 → 120s; n=4 → 120 + 3×120 = 480s.
 * With readyWaitTimeMs = 50s: n=4 → 120 + 3×140 = 540s.
 */
export function computeAndroidLaunchReplyTimeoutMs(
  instanceCount: number,
  readyWaitTimeMs = 0,
): number {
  const count = Math.max(1, Math.floor(instanceCount));
  const openBudgetMs = Math.max(ANDROID_LAUNCH_OPEN_FLOOR_MS, readyWaitTimeMs);
  const perAdditionalMs = ANDROID_LAUNCH_BOOT_BUDGET_MS + openBudgetMs;
  return DEFAULT_ANDROID_LAUNCH_REPLY_TIMEOUT_MS + (count - 1) * perAdditionalMs;
}

/**
 * Availability-block reason for an instance whose launch failed because the
 * game content is not on the VM (android-game-warming Phase 2): a provisioning
 * state, not a crash. The recovery policy never restarts such records; the v2
 * report carries the reason verbatim; a later successful launch for the index
 * (post-warming or campaign relaunch) clears it via the store's `ready`
 * transition.
 */
export const NOT_PROVISIONED_BLOCK_REASON = 'not_provisioned';

/**
 * Classification for `not_provisioned` (android-game-warming Phase 2;
 * re-enumerated in production-readiness Phase 2):
 * defensive lowercase substrings matched against the settle-path failure text
 * — never exact equality, and never boot/adb/emulator wording (those stay
 * crash-class and recover as today).
 *
 * Enumerated C++ error strings that reach `instanceLaunched.error` on the
 * android multi-instance path. Every `BringUpInstance` failure calls
 * `Monitor().SetProcessError(...)` right before returning false, and the
 * launcher surfaces that text via
 * `OnInstanceLaunched(instance, "failure", Monitor().ProcessError())`
 * (apps/playjector/src/PlayJectorLib/source/AndroidLauncher.cpp:195):
 *
 *   not_provisioned-class (game content missing — Playjector never installs;
 *   provisioning warming is the only install path):
 *   - "package not installed" — AndroidLauncher.cpp:371
 *     (`Android::IsPackageInstalled` returned false)
 *
 *   crash-class (matched by no pattern; recover as today):
 *   - "emulator launch failed" — AndroidLauncher.cpp:345
 *   - "emulator boot timeout" — AndroidLauncher.cpp:353
 *   - "adb not ready" — AndroidLauncher.cpp:361
 *   - "package open failed" — AndroidLauncher.cpp:379
 *     (`Android::OpenPackage` failed AFTER presence verification succeeded —
 *      an app-start failure, not missing content. The monkey
 *      "No activities found" detail is logged C++-side only
 *      (AndroidEmulatorEngine.cpp:502) and never reaches the wire, so there
 *      is no package-not-found wording to match on this path.)
 */
export const NOT_PROVISIONED_ERROR_PATTERNS: readonly string[] = [
  // Package-not-found semantics — covers "package not installed".
  'not installed',
];

/** True when a settle-path failure text means the game content is not provisioned. */
const isNotProvisionedFailure = (failure: string): boolean => {
  const text = failure.toLowerCase();
  return NOT_PROVISIONED_ERROR_PATTERNS.some((pattern) => text.includes(pattern));
};

/**
 * The C++ `instanceLaunched` schema lists `error` with default ""
 * (SchemaList.cpp), so a failure reply can carry empty/whitespace text.
 * Treat that as absent so the settle path falls through to the synthesized
 * reason — a falsy failure misclassifies the launch as `instance_failed`
 * and suppresses the `launch_failed` analytics event.
 */
const presentFailureText = (text: string | undefined): string | undefined =>
  text !== undefined && text.trim().length > 0 ? text : undefined;

/** Resolution of one per-instance launch wait: the reply, or a timeout/teardown failure. */
type InstanceLaunchWaitOutcome = {
  event?: InstanceLaunchedEvent;
  failure?: string;
};

type PendingInstanceLaunch = {
  resolve: (outcome: InstanceLaunchWaitOutcome) => void;
  timeoutId: ReturnType<typeof setTimeout>;
};

/**
 * Android multi-instance launch FSM (android-multi-instance-host Phase 3;
 * extracted from GameInstanceLaunchManager in Phase 8, review fix A6).
 *
 * One `launchAndroidInstances` call per campaign launch — no per-session
 * processLaunch, no `prepareDisplays`/`createVirtualDisplayForSession`
 * (C++ owns the monitor/display/VM lifecycle). Each expected instance
 * (0..instanceCount-1) waits on its own `instanceLaunched` reply with a
 * timeout: success → `ready`; error or timeout → `failed` with the reason.
 * No instance can wedge in `launching` (F3).
 *
 * Shares the GameInstanceStore/allocator with the legacy launch manager —
 * records and transitions are identical; only the launch mechanics differ.
 */
export class AndroidInstanceLaunchManager {
  // Per-instance instanceLaunched waiters keyed `${streamId}:${gameInstanceIndex}`.
  private readonly pendingInstanceLaunches = new Map<string, PendingInstanceLaunch>();
  private activeAndroidStreamId: string | undefined;

  constructor(
    private readonly service: IPlayjectorService,
    private readonly store: GameInstanceStore,
    private readonly allocator: GameInstanceAllocator,
    private readonly onRequiredCamerasChanged: (cameras: readonly string[]) => void,
    private readonly onSessionsChanged: (sessions: GameInstanceSessionRecord[]) => void,
  ) {}

  /**
   * Reserve index-only allocations, transition them to `launching`, send the
   * single android launch package, and resolve every expected instance from
   * its `instanceLaunched` reply (or the reply timeout). Per-instance failures
   * are scoped to the failing instance; allocation/package failures throw
   * before any IPC is sent.
   */
  public async launchInstances(
    pkg: ProcessLaunchPackageAndroid,
    options: { campaignId: string; replyTimeoutMs?: number },
  ): Promise<GameInstanceLaunchResult[]> {
    const sessions = this.allocator.allocateForAndroidLaunch({
      count: pkg.instanceCount,
      campaignId: options.campaignId,
      packageName: pkg.package,
    });
    this.activeAndroidStreamId = pkg.streamId;
    for (const session of sessions) {
      // Store-legal path to `launching`; `displayCreating` is an instant
      // pass-through here — the android path creates no displays.
      this.store.transition(session.sessionId, 'displayCreating');
      this.store.transition(session.sessionId, 'launching');
    }
    this.notifySessionsChanged();
    this.onRequiredCamerasChanged(this.requiredCameras());

    const timeoutMs =
      options.replyTimeoutMs ??
      computeAndroidLaunchReplyTimeoutMs(sessions.length, pkg.readyWaitTimeMs ?? 0);
    const launchedAtMs = Date.now();
    Sentry.logger?.warn?.(
      `[AndroidInstanceLaunchManager] Waiting for instanceLaunched ` +
      `campaignId=${options.campaignId} streamId=${pkg.streamId} ` +
      `instanceCount=${sessions.length} readyWaitTimeMs=${pkg.readyWaitTimeMs ?? 0} ` +
      `timeoutMs=${timeoutMs}`,
    );
    const waiters = sessions.map((session) => ({
      session,
      promise: this.waitForInstanceLaunch(pkg.streamId, session.gameInstanceIndex, timeoutMs, launchedAtMs),
    }));

    try {
      await this.service.launchAndroidInstances(pkg);
    } catch (error) {
      // The single launch command itself failed (e.g. Playjector unavailable):
      // every expected instance resolves failed with the same reason.
      this.failPendingInstanceLaunches(
        pkg.streamId,
        error instanceof Error ? error.message : String(error),
      );
    }

    const results: GameInstanceLaunchResult[] = [];
    for (const { session, promise } of waiters) {
      results.push(this.settleInstanceLaunch(session, await promise));
    }
    this.notifySessionsChanged();
    return results;
  }

  /**
   * Route an `instanceLaunched` reply to the pending wait for that
   * (`streamId`, `gameInstanceIndex`) pair. Returns true when matched; false
   * for stale/late replies (already timed out or unknown stream) — a safe
   * no-op, matching `handleLaunchReply`.
   */
  public handleInstanceLaunched(event: InstanceLaunchedEvent): boolean {
    const key = this.instanceLaunchKey(event.streamId, event.gameInstanceIndex);
    const pending = this.pendingInstanceLaunches.get(key);
    if (!pending) {
      Sentry.logger?.warn?.(
        `[AndroidInstanceLaunchManager] Late/unknown instanceLaunched ` +
        `streamId=${event.streamId} instance=${event.gameInstanceIndex} ` +
        `result=${event.result} activeStream=${this.activeAndroidStreamId ?? 'none'}`,
      );
      return false;
    }
    this.pendingInstanceLaunches.delete(key);
    clearTimeout(pending.timeoutId);
    pending.resolve({ event });
    return true;
  }

  /** Stream id of the active android launch (undefined after cleanup). */
  public getActiveAndroidStreamId(): string | undefined {
    return this.activeAndroidStreamId;
  }

  /**
   * Fail every in-flight instance wait for `streamId` with `reason`. Used to
   * surface an aggregate whole-launch failure (a `hostPavilionLaunchReplyMSG`
   * FAILURE with no `sessionId`) immediately with the real reason, instead of
   * leaving each waiter to hit its 120s per-instance reply timeout. Late/absent
   * waiters are a safe no-op. Unlike `abortPendingLaunches`, this does not
   * clear the active stream or unregister it — teardown/cleanup owns that.
   */
  public failActiveStreamLaunches(streamId: string, reason: string): void {
    this.failPendingInstanceLaunches(streamId, reason);
  }

  /**
   * Abort every in-flight instance wait (teardown) and clear the active
   * stream — unregisters it on the service so a late echo from the torn-down
   * stream never reaches the next campaign's consumers (Phase 8, review fix
   * S1). Called by the legacy launch manager's `cleanup()`.
   */
  public abortPendingLaunches(): void {
    for (const [key, pending] of Array.from(this.pendingInstanceLaunches)) {
      this.pendingInstanceLaunches.delete(key);
      clearTimeout(pending.timeoutId);
      pending.resolve({ failure: 'Instance launch aborted: teardown' });
    }
    if (this.activeAndroidStreamId) {
      this.service.unregisterAndroidStream(this.activeAndroidStreamId);
    }
    this.activeAndroidStreamId = undefined;
  }

  private waitForInstanceLaunch(
    streamId: string,
    gameInstanceIndex: number,
    timeoutMs: number,
    launchedAtMs: number,
  ): Promise<InstanceLaunchWaitOutcome> {
    const key = this.instanceLaunchKey(streamId, gameInstanceIndex);
    return new Promise((resolve) => {
      // Never silently overwrite an existing waiter for the same key: settle
      // the previous one failed first so its launch resolves instead of
      // hanging until the reply timeout (Phase 8, review nit N3).
      const existing = this.pendingInstanceLaunches.get(key);
      if (existing) {
        console.warn(
          `[AndroidInstanceLaunchManager] Duplicate launch wait for ${key} — failing the previous waiter`,
        );
        this.pendingInstanceLaunches.delete(key);
        clearTimeout(existing.timeoutId);
        existing.resolve({ failure: `Superseded by a duplicate launch wait (stream ${streamId}, instance ${gameInstanceIndex})` });
      }
      const timeoutId = setTimeout(() => {
        this.pendingInstanceLaunches.delete(key);
        const elapsedMs = Date.now() - launchedAtMs;
        const failure =
          `Timed out after ${timeoutMs}ms waiting for instanceLaunched ` +
          `(stream ${streamId}, instance ${gameInstanceIndex})`;
        Sentry.logger?.error?.(
          `[AndroidInstanceLaunchManager] ${failure} elapsedMs=${elapsedMs}`,
        );
        resolve({ failure });
      }, timeoutMs);
      this.pendingInstanceLaunches.set(key, { resolve, timeoutId });
    });
  }

  private settleInstanceLaunch(
    session: GameInstanceSessionRecord,
    outcome: InstanceLaunchWaitOutcome,
  ): GameInstanceLaunchResult {
    const { sessionId, gameInstanceIndex } = session;
    const current = this.store.getSession(sessionId);
    if (!current || current.status !== 'launching') {
      // Torn down (or otherwise transitioned) while the reply was in flight.
      return {
        sessionId,
        gameInstanceIndex,
        succeeded: false,
        failure: outcome.failure ?? 'Instance session torn down before launch completed',
      };
    }
    if (outcome.event && outcome.event.result === 'success') {
      // The shadowCamName echo is the wire truth. Phase 5: persist a differing
      // echo onto the record so `resolveInstanceCamera` and every downstream
      // consumer (capture routing, required cameras) prefer it over the derived
      // expected name. The mismatch is still logged — it signals host/C++
      // naming-contract drift — but never blocks the ready transition.
      if (outcome.event.shadowCamName && outcome.event.shadowCamName !== current.shadowCamName) {
        console.warn(
          `[AndroidInstanceLaunchManager] instanceLaunched shadowCamName echo ` +
          `"${outcome.event.shadowCamName}" differs from expected "${current.shadowCamName}" ` +
          `(stream ${outcome.event.streamId}, instance ${gameInstanceIndex})`,
        );
        this.store.recordShadowCamEcho(sessionId, outcome.event.shadowCamName);
      }
      // Per-instance audio requires a live AudioCaster cable (AddInstance). An empty
      // echo means Allocate failed (often \\.\AudioCaster control missing on an old
      // driver). Fail-closed: do not keep the allocator stamp — that label was never
      // created, and peer cast would open a silent/missing endpoint.
      const echoedAudio = outcome.event.audioDeviceName ?? '';
      if (!echoedAudio) {
        const failure =
          `Instance ${gameInstanceIndex} AudioCaster cable allocate failed ` +
          `(empty audioDeviceName echo; install/update AudioCaster with multi-instance control)`;
        console.error(`[AndroidInstanceLaunchManager] ${failure}`);
        this.store.transition(sessionId, 'failed', failure);
        return { sessionId, gameInstanceIndex, succeeded: false, failure };
      }
      if (echoedAudio !== current.audioDeviceName) {
        console.warn(
          `[AndroidInstanceLaunchManager] instanceLaunched audioDeviceName echo ` +
          `"${echoedAudio}" differs from expected "${current.audioDeviceName ?? ''}" ` +
          `(stream ${outcome.event.streamId}, instance ${gameInstanceIndex})`,
        );
      }
      this.store.recordAudioDeviceEcho(sessionId, echoedAudio);
      this.store.transition(sessionId, 'ready');
      return { sessionId, gameInstanceIndex, succeeded: true };
    }
    const failure = presentFailureText(outcome.failure)
      ?? presentFailureText(outcome.event?.error)
      ?? `Instance ${gameInstanceIndex} launch failed: ${outcome.event?.result ?? 'unknown result'}`;
    this.store.transition(sessionId, 'failed', failure);
    // android-game-warming Phase 2: a package-not-installed failure is
    // a provisioning state, not a crash — mark it so the recovery policy never
    // restarts the instance and the v2 report shows 'not_provisioned'.
    if (isNotProvisionedFailure(failure)) {
      this.store.setAvailabilityBlockReason(sessionId, NOT_PROVISIONED_BLOCK_REASON);
    }
    return { sessionId, gameInstanceIndex, succeeded: false, failure };
  }

  private failPendingInstanceLaunches(streamId: string, reason: string): void {
    for (const [key, pending] of Array.from(this.pendingInstanceLaunches)) {
      if (!key.startsWith(`${streamId}:`)) continue;
      this.pendingInstanceLaunches.delete(key);
      clearTimeout(pending.timeoutId);
      pending.resolve({ failure: reason });
    }
  }

  private instanceLaunchKey(streamId: string, gameInstanceIndex: number): string {
    return `${streamId}:${gameInstanceIndex}`;
  }

  private requiredCameras(): string[] {
    return this.store.getSessions()
      .map((s) => s.shadowCamName)
      .filter((camera): camera is string => Boolean(camera));
  }

  private notifySessionsChanged(): void {
    this.onSessionsChanged(this.store.getSessions());
  }
}
